import logProPurchaseGetStartedClicked from '@alltrails/analytics/events/logProPurchaseGetStartedClicked';
import logProPurchasePageViewed from '@alltrails/analytics/events/logProPurchasePageViewed';
import NoSsr from '@material-ui/core/NoSsr';
import Typography from 'components/Typography';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import useUser from 'hooks/useUser';
import { lazy, Suspense, useEffect, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import Coupon from 'types/Coupon';
import CurrencyCode from 'types/CurrencyCode';
import Plan from 'types/Plan';
import { getPurchaseScreenTypePlan } from 'utils/amplitudeHelper';
import { wrapUrlSafe } from 'utils/language_support_util';
import { track } from 'utils/OptimizelyHelpers';
import { getPriceTotal, isTrial } from 'utils/PlanUtils';
import ProFeatureComparisonTable from 'components/ProFeatureComparisonTable';
import chevronDownBouncing from './chevron-down-bouncing.svg';
import * as styles from './SimpleProLandingPage.module.scss';
import heroJpg from './hero-2400w.jpg';
import heroAvif from './hero-2400w.avif';
import heroWebp from './hero-2400w.webp';

const ChevronDownBouncing = lazy(() => import('./ChevronDownBouncing'));

function PrimaryLink({ children, coupon, plan, to }: { children: ReactNode; plan: Plan; coupon: Coupon; to: string }) {
  const languageRegionCode = useLanguageRegionCode();

  return (
    <Link
      className={styles.link}
      onClick={() => {
        logProPurchaseGetStartedClicked({
          purchase_screen_type_plan: getPurchaseScreenTypePlan(plan, coupon)
        });
        track('Green Proceed CTA Button Tapped');
      }}
      to={wrapUrlSafe(to, languageRegionCode)}
    >
      {children}
    </Link>
  );
}

// Desktop: https://app.zeplin.io/project/6266970577375811a811b641/screen/626699f31a5c6950eadeccd7
// Mobile: https://app.zeplin.io/project/6266970577375811a811b641/screen/62669ff25f0fc0159880417c
// Figma: https://www.figma.com/file/vJ30CAAm8qdbDO0j5C4Ce7/Web-Pro-Purchase-Flow
// Fallback photo: https://app.zeplin.io/project/6266970577375811a811b641/screen/626abd5ad29d6a953539e4fd

type Props = {
  buttonText: ReactNode;
  coupon?: Coupon;
  headline: ReactNode;
  subhead: ReactNode;
  title: ReactNode;
  userId?: number;
  plan: Plan;
};

function SimpleProLandingPageBase({ buttonText, coupon, headline, subhead, title, userId, plan }: Props) {
  useEffect(() => {
    logProPurchasePageViewed({
      purchase_screen_type_plan: getPurchaseScreenTypePlan(plan, coupon)
    });
  }, [plan, coupon]);

  const user = useUser();

  const features = [
    { pro: false, text: <FormattedMessage defaultMessage="Navigate on the trail" /> },
    { pro: false, text: <FormattedMessage defaultMessage="Favorite trails and create lists" /> },
    { pro: true, text: <FormattedMessage defaultMessage="Download offline maps" /> },
    { pro: true, text: <FormattedMessage defaultMessage="Get alerts for wrong turns" /> },
    { new: true, pro: true, text: <FormattedMessage defaultMessage="Find trails by distance from you" /> },
    { pro: true, text: <FormattedMessage defaultMessage="Unlock more map details" /> },
    { pro: true, text: <FormattedMessage defaultMessage="Print maps for backup" /> },
    { pro: true, text: <FormattedMessage defaultMessage="Bring trails to life with 3D maps" /> },
    { pro: true, text: <FormattedMessage defaultMessage="Explore ad free" /> },
    { pro: true, text: <FormattedMessage defaultMessage="Give back with 1% for the Planet" /> }
  ];

  const params = new URLSearchParams(__AT_DATA__.search);

  if (coupon) {
    params.set('k', coupon.code);
  }

  if (userId) {
    params.set('uid', String(userId));
  }

  const search = params.toString();

  const to = user || userId ? `/pro/checkout${search.length > 0 ? `?${search}` : ''}` : `/signup${search.length > 0 ? `?${search}` : ''}`;

  return (
    <div className={styles.wrapper}>
      <div className={styles.hero}>
        <picture className={styles.picture}>
          <source srcSet={heroAvif} type="image/avif" />
          <source srcSet={heroWebp} type="image/webp" />
          <img alt="" loading="lazy" src={heroJpg} />
        </picture>
      </div>
      <div className={styles.content}>
        <div className={styles.copy}>
          <div className={styles.headline}>{headline}</div>
          <div className={styles.title}>{title}</div>
          <div className={styles.subhead}>{subhead}</div>
          <div className={styles.button}>
            <PrimaryLink plan={plan} coupon={coupon} to={to}>
              {buttonText}
            </PrimaryLink>
            <Typography className={styles.cancel} component="div" mb="8" variant="caption">
              <FormattedMessage defaultMessage="Cancel anytime." />
            </Typography>
          </div>
          <Typography className={styles.lottie} component="div" mb="8">
            <NoSsr>
              <Suspense fallback={<img alt="" src={chevronDownBouncing} />}>
                <ChevronDownBouncing />
              </Suspense>
            </NoSsr>
          </Typography>
        </div>
        <ProFeatureComparisonTable features={features} />
      </div>
    </div>
  );
}

type SimpleProLandingPageProps = {
  coupon?: Coupon;
  currencyCode: CurrencyCode;
  planTypes: Plan[];
  userId?: number;
};

export default function SimpleProLandingPage({ currencyCode, planTypes, coupon, userId }: SimpleProLandingPageProps) {
  const languageRegionCode = useLanguageRegionCode();

  const formatter = new Intl.NumberFormat(languageRegionCode, {
    style: 'currency',
    currency: currencyCode
  });

  const plan = planTypes[0];

  if (coupon) {
    const discountedPricePerYear = getPriceTotal(plan, coupon);

    // Desktop: https://zpl.io/ykXrOZJ
    // Mobile: https://zpl.io/9qgXqNp
    return (
      <SimpleProLandingPageBase
        buttonText={<FormattedMessage defaultMessage="Save today" />}
        coupon={coupon}
        headline={<FormattedMessage defaultMessage="Limited time only" />}
        subhead={
          discountedPricePerYear === 0 ? (
            <FormattedMessage
              defaultMessage="Just {discountedPricePerYear} for one year"
              values={{
                discountedPricePerYear: formatter.format(discountedPricePerYear)
              }}
            />
          ) : (
            <FormattedMessage
              defaultMessage="Just <s>{priceTotal}</s> {discountedPricePerYear} for one year (that’s {monthlyCost} a month)"
              values={{
                discountedPricePerYear: formatter.format(discountedPricePerYear),
                monthlyCost: formatter.format(discountedPricePerYear / 12),
                priceTotal: formatter.format(Number(plan.priceTotal)),
                s: (chunks: string) => <s className={styles.s}>{chunks}</s>
              }}
            />
          )
        }
        title={
          <FormattedMessage
            defaultMessage="Save {percent, number, ::percent} on AllTrails Pro"
            values={{ percent: coupon.discountPercentage / 100 }}
          />
        }
        userId={userId}
        plan={plan}
      />
    );
  }

  const annualCost = Number(plan.priceTotal);
  const monthlyCost = Number(plan.priceMonth);

  if (isTrial(plan)) {
    return (
      // Desktop: https://zpl.io/PqOJONM
      // Mobile: https://zpl.io/o1nvD3m
      <SimpleProLandingPageBase
        buttonText={<FormattedMessage defaultMessage="Start your free trial" />}
        headline={<FormattedMessage defaultMessage="AllTrails Pro" />}
        subhead={
          <FormattedMessage
            defaultMessage="Then only {annualCost} a year (that’s {monthlyCost} a month)"
            values={{
              annualCost: formatter.format(annualCost),
              monthlyCost: formatter.format(monthlyCost)
            }}
          />
        }
        title={<FormattedMessage defaultMessage="Your first week’s on us" />}
        userId={userId}
        plan={plan}
      />
    );
  }

  return (
    // Desktop: https://zpl.io/wqB84gd
    // Mobile: https://zpl.io/p1mp10E
    <SimpleProLandingPageBase
      buttonText={<FormattedMessage defaultMessage="Get started" />}
      headline={<FormattedMessage defaultMessage="AllTrails Pro" />}
      subhead={
        <FormattedMessage
          defaultMessage="Only {annualCost} a year (that’s {monthlyCost} a month)"
          values={{
            annualCost: formatter.format(annualCost),
            monthlyCost: formatter.format(monthlyCost)
          }}
        />
      }
      title={<FormattedMessage defaultMessage="Confidence that travels light" />}
      userId={userId}
      plan={plan}
    />
  );
}
